import { useState } from "react";
import { useParams } from "react-router";
import { DJ_ROLLEN, HUNDEEINSATZARTEN, WAFFE_PFLICHT, WAFFE_WAEHLBAR, type DjRolle } from "@dj/domain/drueckjagd";
import { Dialog } from "../design/Dialog";
import { Ankreuzfeld, Auswahl, Klappliste, Textfeld } from "../design/Formular";
import { Abweisung } from "../design/Hinweis";
import { Kachel, Knopf } from "../design/Kachel";
import { Symbol } from "../design/Symbol";
import { abweisungsgrund, befehl } from "../plattform/api";
import { useSammlung, type Zeile } from "../plattform/lesemodelle";
import { AnsichtDerDrueckjagd } from "./AnsichtDerDrueckjagd";
import { Abschnitt, Leer, name, namen, useEigeneRollen, useRevier, useRevierSammlung, wert, type Person } from "./teile";

/**
 * Einteilung (oberflaeche.md §6, Domänenmodell Aggregat Einteilung): Mitwirkende mit Rollen und „führt Waffe“, je Stand
 * Schütze und Anstellgruppe, Anstellgruppen, Treibergruppen und Hundeeinsätze. Jede Änderung ist ein Befehl
 * (befehle.md §6.3); eine Abweisung zeigt die Ansicht mit Grund.
 *
 * Bedienelemente ohne Wirkung erscheinen nicht (Anforderungen §7a K98, oberflaeche.md §6, V-22): Ändern darf die
 * Einteilung nur der Jagdherr des Reviers (`drueckjagd/03`–`05`). Alle anderen Leser, etwa der Jagdleiter, sehen
 * dieselben Einträge lesend, ohne „Einteilen“, „Anstellgruppe“, „Treibergruppe“, „Hundeeinsatz“ und ohne Felder.
 * Die Handlungen stehen fest unter der Liste (design/README §9.8, Referenz `liste__unten`; Bericht N6 F10).
 */
const UNBESETZT = "—";

type Dialogart = "Einteilen" | "Anstellgruppe" | "Treibergruppe" | "Hundeeinsatz";

function liste(text: string): string[] {
  return text.split(",").map((t) => t.trim()).filter(Boolean);
}

/** Wer die Einteilung ändern darf: der Jagdherr des Reviers, in dem die Person mitwirkt (befehle.md §6.3, ADR-0016). */
export function darfEinteilen(rollen: readonly string[]): boolean {
  return rollen.includes("Jagdherr");
}

const kennung = (z: Zeile) => (z["Name"] as Person).Kennung;

/** Handlungen der Einteilung, fest unter der Liste (vier Kacheln, zwei Spalten). */
export function EinteilungsHandlungen({ onWahl }: { onWahl: (art: Dialogart) => void }) {
  return (
    <div className="raster raster--2 liste__unten" data-gleich="">
      <Kachel art="kompakt" symbol="hinzufuegen" begriff="Einteilen" onPress={() => onWahl("Einteilen")} />
      <Kachel art="kompakt" symbol="ansteller" begriff="Anstellgruppe" onPress={() => onWahl("Anstellgruppe")} />
      <Kachel art="kompakt" symbol="treiberfuehrer" begriff="Treibergruppe" onPress={() => onWahl("Treibergruppe")} />
      <Kachel art="kompakt" symbol="hund" begriff="Hundeeinsatz" onPress={() => onWahl("Hundeeinsatz")} />
    </div>
  );
}

/** Ein Mitwirkender: für den Jagdherrn Rollen und „führt Waffe“ als Ankreuzfelder und „Entfernen“, sonst als Angaben. */
export function Mitwirkender({ p, bearbeitbar, senden }: { p: Zeile; bearbeitbar: boolean; senden: Senden }) {
  const rollen = p["Rollen"] as DjRolle[];
  const pflicht = rollen.some((r) => WAFFE_PFLICHT.includes(r));
  return (
    <article className={bearbeitbar ? "eintrag eintrag--gegliedert platte" : "eintrag platte"} aria-label={name(p["Name"])}>
      <h3><Symbol name="teilnehmer" />{name(p["Name"])}</h3>
      {bearbeitbar ? (
        <>
          <div className="filter filter--lang">
            {DJ_ROLLEN.map((r) => (
              <Ankreuzfeld key={r} label={r} an={rollen.includes(r)}
                onChange={(an) => void senden(an ? "RolleZuweisen" : "RolleEntziehen", { Teilnehmer: kennung(p), Rolle: r })} />
            ))}
          </div>
          <div className="filter">
            <Ankreuzfeld label="führt Waffe" an={p["führt Waffe"] === true}
              onChange={(an) => void senden("WaffenfuehrungSetzen", { Teilnehmer: kennung(p), "führt Waffe": an })} />
            {pflicht ? <span className="eintrag__meta">immer gesetzt</span> : null}
            <Knopf art="planung-zweit" symbol="schliessen" onPress={() => void senden("MitwirkendenEntfernen", { Teilnehmer: kennung(p) })}>Entfernen</Knopf>
          </div>
        </>
      ) : (
        <dl className="angaben">
          <div><dt>Rollen</dt><dd>{wert(rollen)}</dd></div>
          <div><dt>führt Waffe</dt><dd>{wert(p["führt Waffe"] === true)}</dd></div>
        </dl>
      )}
    </article>
  );
}

/** Ein Stand: für den Jagdherrn Schütze und Anstellgruppe als Auswahl und „Nachbarstände“, sonst als Angaben. */
export function StandDerEinteilung({ s, schuetzen, gruppen, bearbeitbar, senden, onNachbarn }: {
  s: Zeile; schuetzen: readonly Zeile[]; gruppen: readonly Zeile[]; bearbeitbar: boolean; senden: Senden; onNachbarn: (s: Zeile) => void;
}) {
  const schuetze = s["Schütze"] as Person | null;
  const gruppe = (s["Anstellgruppe"] as string | null) ?? null;
  return (
    <article className="eintrag platte" aria-label={`Stand ${String(s["Stand"])}`}>
      <h3><Symbol name="stand" />Stand {String(s["Stand"])}</h3>
      {bearbeitbar ? (
        <>
          <Klappliste label="Schütze" wert={schuetze?.Kennung ?? UNBESETZT}
            optionen={[{ wert: UNBESETZT, text: "unbesetzt" }, ...schuetzen.map((p) => ({ wert: kennung(p), text: name(p["Name"]) }))]}
            onChange={(w) => void (w === UNBESETZT
              ? senden("ZuweisungAufheben", { Stand: s._id, Zuweisung: "Schütze" })
              : senden("StandSchuetzenZuweisen", { Stand: s._id, "Schütze": w }))} />
          <Klappliste label="Anstellgruppe" wert={gruppe ?? UNBESETZT}
            optionen={[{ wert: UNBESETZT, text: "keine" }, ...gruppen.map((g) => ({ wert: String(g["Anstellgruppe"]), text: String(g["Anstellgruppe"]) }))]}
            onChange={(w) => void (w === UNBESETZT
              ? senden("ZuweisungAufheben", { Stand: s._id, Zuweisung: "Ansteller" })
              : senden("StandAnstellerZuweisen", { Stand: s._id, Anstellgruppe: w }))} />
          <div className="eintrag__meta">Ansteller: {namen(s["Ansteller"])}</div>
          <Knopf art="planung-zweit" symbol="karte" onPress={() => onNachbarn(s)}>Nachbarstände</Knopf>
        </>
      ) : (
        <dl className="angaben">
          <div><dt>Schütze</dt><dd>{schuetze ? name(schuetze) : "unbesetzt"}</dd></div>
          <div><dt>Anstellgruppe</dt><dd>{gruppe ?? "keine"}</dd></div>
          <div><dt>Ansteller</dt><dd>{namen(s["Ansteller"])}</dd></div>
        </dl>
      )}
    </article>
  );
}

export function Einteilung() {
  const { id = "" } = useParams();
  const revier = useRevier(id);
  const bearbeitbar = darfEinteilen(useEigeneRollen(id));
  const mitwirkende = useRevierSammlung(id, revier, "mitwirkende");
  const staende = useRevierSammlung(id, revier, "standzuweisungen");
  const gruppen = useRevierSammlung(id, revier, "anstellgruppen");
  const treiber = useRevierSammlung(id, revier, "treibergruppen");
  const hunde = useRevierSammlung(id, revier, "hundeeinsaetze");
  const [grund, setGrund] = useState<string | null>(null);
  const [dialog, setDialog] = useState<null | Dialogart>(null);
  const [nachbarn, setNachbarn] = useState<Zeile | null>(null);
  const basis = { "Drückjagd": id, Revier: revier };

  async function senden(pfad: string, daten: Record<string, unknown>): Promise<boolean> {
    setGrund(null);
    const a = await befehl(pfad, { ...basis, ...daten });
    if (!a.ok) setGrund(abweisungsgrund(a));
    return a.ok;
  }

  const personen = [...mitwirkende.daten].sort((a, b) => name(a["Name"]).localeCompare(name(b["Name"]), "de"));
  const schuetzen = personen.filter((p) => (p["Rollen"] as string[]).includes("Schütze"));

  return (
    <AnsichtDerDrueckjagd drueckjagdId={id} name="Einteilung" unten={bearbeitbar ? <EinteilungsHandlungen onWahl={setDialog} /> : undefined}>
      <Abweisung grund={grund} />

      <Abschnitt name="Mitwirkende" symbol="teilnehmer">
        {personen.length === 0 && !mitwirkende.laedt ? <Leer text="Noch niemand eingeteilt" symbol="teilnehmer" /> : null}
        {personen.map((p) => <Mitwirkender key={p._id} p={p} bearbeitbar={bearbeitbar} senden={senden} />)}
      </Abschnitt>

      <Abschnitt name="Stände" symbol="stand">
        {staende.daten.length === 0 && !staende.laedt ? <Leer text="Das Revier hat noch keine Stände" symbol="stand" /> : null}
        {staende.daten.map((s) => (
          <StandDerEinteilung key={s._id} s={s} schuetzen={schuetzen} gruppen={gruppen.daten} bearbeitbar={bearbeitbar} senden={senden} onNachbarn={setNachbarn} />
        ))}
      </Abschnitt>

      <Abschnitt name="Anstellgruppen" symbol="ansteller">
        {gruppen.daten.map((g) => (
          <article key={g._id} className="eintrag platte" aria-label={String(g["Anstellgruppe"])}>
            <h3><Symbol name="ansteller" />{String(g["Anstellgruppe"])}</h3>
            <div className="eintrag__meta">Ansteller: {namen(g["Ansteller"])} · Stände: {(g["Stände"] as string[]).join(", ") || "—"}</div>
          </article>
        ))}
      </Abschnitt>

      <Abschnitt name="Treibergruppen" symbol="treiber">
        {treiber.daten.map((t) => (
          <article key={t._id} className="eintrag platte" aria-label={String(t["Treibergruppe"])}>
            <h3><Symbol name="treiberfuehrer" />{String(t["Treibergruppe"])}</h3>
            <div className="eintrag__meta">{namen(t["Mitglieder"])} · {String(t["Einsatzbereich"] ?? "—")}: {(t["Abschnitte in Folge"] as string[]).join(", ")}</div>
          </article>
        ))}
      </Abschnitt>

      <Abschnitt name="Hundeeinsätze" symbol="hund">
        {hunde.daten.map((h) => (
          <article key={h._id} className="eintrag platte" aria-label={name(h["Hundeführer"])}>
            <h3><Symbol name="hundefuehrer" />{name(h["Hundeführer"])}</h3>
            <div className="eintrag__meta">{String(h["Hundeeinsatzart"])} mit {(h["Hunde"] as string[]).join(", ")} · {String(h["Einsatzbereich bzw. Stand"] ?? "—")}</div>
          </article>
        ))}
      </Abschnitt>

      {bearbeitbar ? (
        <>
          <EinteilenDialog offen={dialog === "Einteilen"} revier={revier} onSchliessen={() => setDialog(null)} senden={senden} />
          <GruppenDialog art={dialog} personen={personen} onSchliessen={() => setDialog(null)} senden={senden} />
          <NachbarDialog stand={nachbarn} staende={staende.daten} onSchliessen={() => setNachbarn(null)} senden={senden} />
        </>
      ) : null}
    </AnsichtDerDrueckjagd>
  );
}

type Senden = (pfad: string, daten: Record<string, unknown>) => Promise<boolean>;

function EinteilenDialog({ offen, revier, onSchliessen, senden }: { offen: boolean; revier: string | null; onSchliessen: () => void; senden: Senden }) {
  const teilnehmer = useSammlung(offen && revier ? `reviere/${revier}/teilnehmeruebersicht` : null);
  const [tid, setTid] = useState<string | null>(null);
  const [rollen, setRollen] = useState<DjRolle[]>([]);
  const [waffe, setWaffe] = useState(false);
  const waehlbar = rollen.some((r) => WAFFE_WAEHLBAR.includes(r) || WAFFE_PFLICHT.includes(r));
  async function speichern() {
    const daten: Record<string, unknown> = { Teilnehmer: tid, Rollen: rollen };
    if (waehlbar) daten["führt Waffe"] = waffe || rollen.some((r) => WAFFE_PFLICHT.includes(r));
    if (await senden("MitwirkendenHinzufuegen", daten)) { setTid(null); setRollen([]); setWaffe(false); onSchliessen(); }
  }
  const aktive = teilnehmer.daten.filter((t) => t["Teilnehmerstatus"] !== "entfernt");
  return (
    <Dialog name="Einteilen" symbol="hinzufuegen" offen={offen} onSchliessen={onSchliessen}>
      <div className="formular">
        <Klappliste label="Teilnehmer" wert={tid} leer="bitte wählen"
          optionen={aktive.map((t) => ({ wert: t._id, text: name(t["Teilnehmer"]) }))} onChange={setTid} />
        <div className="filter filter--lang">
          {DJ_ROLLEN.map((r) => (
            <Ankreuzfeld key={r} label={r} an={rollen.includes(r)} onChange={(an) => setRollen(an ? [...rollen, r] : rollen.filter((x) => x !== r))} />
          ))}
        </div>
        <Ankreuzfeld label="führt Waffe" an={waffe || rollen.some((r) => WAFFE_PFLICHT.includes(r))} onChange={setWaffe} />
        <div className="knopfreihe">
          <Knopf onPress={speichern} isDisabled={!tid || rollen.length === 0}>Speichern</Knopf>
          <Knopf art="zweit" onPress={onSchliessen}>Abbrechen</Knopf>
        </div>
      </div>
    </Dialog>
  );
}

function GruppenDialog({ art, personen, onSchliessen, senden }: { art: string | null; personen: Zeile[]; onSchliessen: () => void; senden: Senden }) {
  const [bezeichnung, setBezeichnung] = useState("");
  const [fuehrer, setFuehrer] = useState<string | null>(null);
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  const [bereich, setBereich] = useState("");
  const [abschnitte, setAbschnitte] = useState("");
  const [hunde, setHunde] = useState("");
  const [einsatzart, setEinsatzart] = useState<string | null>(null);
  const offen = art === "Anstellgruppe" || art === "Treibergruppe" || art === "Hundeeinsatz";
  const mit = (rolle: string) => personen.filter((p) => (p["Rollen"] as string[]).includes(rolle));
  const zuruecksetzen = () => { setBezeichnung(""); setFuehrer(null); setGewaehlt([]); setBereich(""); setAbschnitte(""); setHunde(""); setEinsatzart(null); };
  async function speichern() {
    let ok = false;
    if (art === "Anstellgruppe") ok = await senden("AnstellgruppeBilden", { Anstellgruppe: bezeichnung, Ansteller: gewaehlt });
    if (art === "Treibergruppe") {
      ok = await senden("TreibergruppeBilden", {
        Treibergruppe: bezeichnung, "Treiberführer": fuehrer, Treiber: gewaehlt, Einsatzbereich: bereich || undefined,
        "Abschnitte in Folge": liste(abschnitte),
      });
    }
    if (art === "Hundeeinsatz") {
      ok = await senden("HundeeinsatzFestlegen", {
        "Hundeführer": fuehrer, Hunde: liste(hunde), Hundeeinsatzart: einsatzart, Einsatzbereich: bereich || undefined,
        "Abschnitte in Folge": liste(abschnitte),
      });
    }
    if (ok) { zuruecksetzen(); onSchliessen(); }
  }
  const auswahl = art === "Anstellgruppe" ? mit("Ansteller") : personen;
  return (
    <Dialog name={art ?? "Anstellgruppe"} symbol={art === "Hundeeinsatz" ? "hund" : art === "Treibergruppe" ? "treiberfuehrer" : "ansteller"} offen={offen} onSchliessen={onSchliessen}>
      <div className="formular">
        {art !== "Hundeeinsatz" ? <Textfeld label={art ?? "Anstellgruppe"} wert={bezeichnung} onChange={setBezeichnung} /> : null}
        {art === "Treibergruppe" ? (
          <Klappliste label="Treiberführer" wert={fuehrer} leer="bitte wählen" onChange={setFuehrer}
            optionen={mit("Treiberführer").map((p) => ({ wert: kennung(p), text: name(p["Name"]) }))} />
        ) : null}
        {art === "Hundeeinsatz" ? (
          <>
            <Klappliste label="Hundeführer" wert={fuehrer} leer="bitte wählen" onChange={setFuehrer}
              optionen={personen.map((p) => ({ wert: kennung(p), text: name(p["Name"]) }))} />
            <Auswahl label="Hundeeinsatzart" optionen={HUNDEEINSATZARTEN} wert={einsatzart} onChange={setEinsatzart} />
            <Textfeld label="Hunde" wert={hunde} onChange={setHunde} hilfe="Kennungen der Hunde, durch Komma getrennt" />
          </>
        ) : (
          <div className="filter" role="group" aria-label={art === "Anstellgruppe" ? "Ansteller" : "Treiber"}>
            {auswahl.map((p) => (
              <Ankreuzfeld key={p._id} label={name(p["Name"])} an={gewaehlt.includes(kennung(p))}
                onChange={(an) => setGewaehlt(an ? [...gewaehlt, kennung(p)] : gewaehlt.filter((x) => x !== kennung(p)))} />
            ))}
          </div>
        )}
        {art !== "Anstellgruppe" ? (
          <>
            <Textfeld label="Einsatzbereich" wert={bereich} onChange={setBereich} />
            <Textfeld label="Abschnitte in Folge" wert={abschnitte} onChange={setAbschnitte} hilfe="in der Reihenfolge des Treibens, z. B. 2, 2a, 2b, 3" />
          </>
        ) : null}
        <div className="knopfreihe">
          <Knopf onPress={speichern}>Speichern</Knopf>
          <Knopf art="zweit" onPress={onSchliessen}>Abbrechen</Knopf>
        </div>
      </div>
    </Dialog>
  );
}

function NachbarDialog({ stand, staende, onSchliessen, senden }: { stand: Zeile | null; staende: Zeile[]; onSchliessen: () => void; senden: Senden }) {
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  return (
    <Dialog name="Nachbarstände" symbol="karte" offen={!!stand} onSchliessen={onSchliessen}>
      <div className="formular">
        <div className="filter">
          {staende.filter((s) => s._id !== stand?._id).map((s) => (
            <Ankreuzfeld key={s._id} label={`Stand ${String(s["Stand"])}`} an={gewaehlt.includes(s._id)}
              onChange={(an) => setGewaehlt(an ? [...gewaehlt, s._id] : gewaehlt.filter((x) => x !== s._id))} />
          ))}
        </div>
        <div className="knopfreihe">
          <Knopf onPress={async () => { if (stand && await senden("NachbarstaendeKorrigieren", { Stand: stand._id, "Nachbarstände": gewaehlt })) { setGewaehlt([]); onSchliessen(); } }}>Speichern</Knopf>
          <Knopf art="zweit" onPress={onSchliessen}>Abbrechen</Knopf>
        </div>
      </div>
    </Dialog>
  );
}
